import type { Logger } from 'loglevel';
import * as timer from './timer';

export class TimerObserver<S extends string> {
  /**
   * @param timerKey the timer key.
   * @param logger to log to DataDog.
   */
  constructor(
    private readonly timerKey: string,
    private readonly logger: Logger,
  ) {}

  /**
   * Convert duration to nanoseconds.
   * Datadog advises to remap durations of any kind on this attribute:
   * @see https://docs.datadoghq.com/logs/log_configuration/attributes_naming_convention/#performance
   * @returns nanoseconds.
   */
  static fromMilliseconds(ms: number): number {
    return 1e6 * ms;
  }

  /**
   * Get time between the specified timer step and the step before it.
   * @return duration of step in milliseconds.
   */
  private getDurationOfStep(step: S): number {
    const duration = timer.getDurationOfStep(this.timerKey, step);
    return duration;
  }

  /**
   * @param type required for DataDog dashboard queries.
   * @param params.duration duration of step or timer in milliseconds.
   * @returns a human-friendly message that reads well in DataDog Logs Explorer.
   */
  private createLogMessage(
    type: 'start' | 'step' | 'finish' | 'total',
    params: { step?: S; duration?: number },
  ): string {
    const { step, duration } = params;

    const parts = [type, this.timerKey];

    if (typeof step !== 'undefined') {
      parts.push(step);
    }

    if (typeof duration !== 'undefined') {
      const durationString = `${duration.toFixed(0)}ms`;
      parts.push(durationString);
    }

    return parts.join(' ');
  }

  /**
   * Get total time of finished timer.
   * @return duration of timer in milliseconds.
   */
  getDurationOfTimer(): number {
    const duration = timer.getResult(this.timerKey).total;
    return duration;
  }

  private logDurationOfStep(step: S, type: 'step' | 'finish'): void {
    const duration = this.getDurationOfStep(step);

    /**
     * Send to DataDog Logs.
     */
    this.logger.info(this.createLogMessage(type, { step, duration }), {
      timer: this.timerKey,
      step,
      duration: TimerObserver.fromMilliseconds(duration),
    });
  }

  private logDurationOfTimer(): void {
    const duration = this.getDurationOfTimer();

    /**
     * Send total to DataDog Logs.
     */
    this.logger.info(this.createLogMessage('total', { duration }), {
      timer: this.timerKey,
      duration: TimerObserver.fromMilliseconds(duration),
    });
  }

  /**
   * Start timer.
   */
  start(step: S, startTimestamp?: number): void {
    timer.start(this.timerKey, step, startTimestamp);

    this.logger.info(this.createLogMessage('start', { step }), {
      timer: this.timerKey,
      step,
    });
  }

  /**
   * Step timer and log duration to DataDog.
   */
  step(step: S): void {
    timer.setStep(this.timerKey, step);

    this.logDurationOfStep(step, 'step');
  }

  /**
   * Finish timer and log step and total durations to DataDog.
   */
  finish(step: S): void {
    timer.finish(this.timerKey, step);

    this.logDurationOfStep(step, 'finish');
    this.logDurationOfTimer();
  }
}
